// Checagem pura de Permissão de Módulo (issue #71). Usada pelo servidor —
// para recusar um Comando fora do Módulo que o Perfil do usuário permite — e
// pelo cliente, para esconder navegação e controles de escrita. As duas
// pontas importam as mesmas funções: duas implementações da mesma regra de
// autorização é como uma checagem de segurança apodrece sem ninguém notar.
//
// Puro (só Snapshot + ids), sem dependência de `comandos/index.ts` — evita um
// ciclo de import com `perfil.ts`, que também precisa desta checagem.

import type { Id } from "../domain/ids.ts";
import { concedeGravacao, type Modulo, type NivelPermissao } from "../domain/schema.ts";
import type { Snapshot } from "../domain/snapshot.ts";

function nivelDoUsuario(
  s: Snapshot,
  usuarioId: Id,
  modulo: Modulo,
): NivelPermissao {
  const u = s.usuarios.find((x) => x.id === usuarioId);
  // Usuário inativado não concede nada, mesmo com sessão ainda válida — a
  // inativação (ex-funcionário) precisa valer de verdade, não só tirar o
  // login novo (usuarioParaLogin já filtrava `ativo`, mas só na hora de
  // logar; uma sessão aberta antes de inativar continuava tendo acesso).
  if (!u?.ativo || !u.perfilId) return "nenhum";
  // Perfil inativo não concede nada — "Inativar" na tela de Perfis precisa
  // valer de verdade pra quem já estava nele, não só sumir do dropdown.
  const perfil = s.perfis.find((p) => p.id === u.perfilId);
  if (!perfil?.ativo) return "nenhum";
  const p = s.permissoesModulo.find(
    (pm) => pm.perfilId === u.perfilId && pm.modulo === modulo,
  );
  return p?.nivel ?? "nenhum";
}

/** O Perfil do usuário tem ao menos leitura neste Módulo? */
export function podeLerModulo(s: Snapshot, usuarioId: Id, modulo: Modulo): boolean {
  return nivelDoUsuario(s, usuarioId, modulo) !== "nenhum";
}

/** O Perfil do usuário tem gravação neste Módulo? */
export function podeGravarNoModulo(
  s: Snapshot,
  usuarioId: Id,
  modulo: Modulo,
): boolean {
  return concedeGravacao(nivelDoUsuario(s, usuarioId, modulo));
}
